import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const DEBOUNCE_MS = 800;

const PAGE_LABELS = {
  '/': 'Home',
  '/pricing': 'Pricing',
  '/billing': 'Billing',
  '/progress': 'Progress',
  '/dashboard': 'Dashboard',
  '/study-guide': 'Study guide',
  '/glossary': 'Glossary',
  '/blog': 'Blog',
  '/login': 'Login',
  '/signup': 'Sign up',
  '/terms': 'Terms',
  '/privacy': 'Privacy',
  '/edit-profile': 'Edit profile',
};

let pending = null;
let timer = null;
let lastUid = '';

export function pageLabel(pathname = '') {
  const path = String(pathname || '/').split('?')[0].replace(/\/+$/, '') || '/';
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith('/quiz')) return 'Quiz';
  if (path.startsWith('/blog/')) return 'Blog article';
  if (path.startsWith('/admin')) return 'Admin';
  return path;
}

export function formatLiveContext({
  pageLabel: label,
  quizMode,
  phase,
  questionNum,
  questionTotal,
  topicLabel,
}) {
  if (phase === 'results') {
    const base = quizMode === 'session' ? 'Mock exam' : topicLabel || 'Topic drill';
    return `${base} · results`;
  }
  if (phase === 'preflight') return 'Mock exam · preflight';
  if (phase === 'loading') {
    const base = quizMode === 'session' ? 'Mock exam' : topicLabel || 'Quiz';
    return `${base} · loading`;
  }
  if (phase === 'exam' && questionNum != null && questionTotal) {
    const base =
      quizMode === 'session' ? 'Mock exam' : quizMode === 'review' ? 'Review queue' : topicLabel || 'Topic drill';
    return `${base} · Q ${questionNum}/${questionTotal}`;
  }
  return label || 'Browsing';
}

export function syncLiveActivity(patch = {}) {
  const user = auth.currentUser;
  if (!user?.uid || typeof window === 'undefined') return;
  lastUid = user.uid;
  pending = { ...pending, ...patch, liveUpdatedAt: Timestamp.now() };
  if (timer) return;
  timer = window.setTimeout(flushLiveActivity, DEBOUNCE_MS);
}

async function flushLiveActivity() {
  timer = null;
  const uid = lastUid;
  const payload = pending;
  pending = null;
  if (!uid || !payload) return;
  try {
    await setDoc(doc(db, 'userProfiles', uid), payload, { merge: true });
  } catch {
    /* ignore quota / offline */
  }
}

export function syncPageActivity(pathname) {
  const path = String(pathname || '/').split('?')[0] || '/';
  const label = pageLabel(path);
  if (path.startsWith('/quiz')) {
    syncLiveActivity({ currentPath: path, currentPageLabel: label });
    return;
  }
  syncLiveActivity({
    currentPath: path,
    currentPageLabel: label,
    liveContext: label,
    liveQuizMode: null,
    liveQuizPhase: null,
    liveQuestionNum: null,
    liveQuestionTotal: null,
    liveQuizTopic: null,
  });
}

export function syncQuizActivity({
  slug,
  topicLabel,
  isSession,
  isReview,
  started,
  isQuizComplete,
  loading,
  currentQuestion,
  questionTotal,
}) {
  const path = `/quiz/${slug || 'session'}`;
  let mode = 'topic';
  if (isSession) mode = 'session';
  if (isReview) mode = 'review';

  let phase = 'browse';
  if (loading) phase = 'loading';
  else if (isQuizComplete) phase = 'results';
  else if (isSession && !started) phase = 'preflight';
  else if (started) phase = 'exam';

  const context = formatLiveContext({
    pageLabel: pageLabel(path),
    quizMode: mode,
    phase,
    questionNum: started && !isQuizComplete ? currentQuestion + 1 : null,
    questionTotal: questionTotal || null,
    topicLabel,
  });

  syncLiveActivity({
    currentPath: path,
    currentPageLabel: pageLabel(path),
    liveContext: context,
    liveQuizMode: mode,
    liveQuizPhase: phase,
    liveQuestionNum: started && !isQuizComplete ? currentQuestion + 1 : null,
    liveQuestionTotal: questionTotal || null,
    liveQuizTopic: slug || null,
  });
}

export function liveNowLabel(user = {}) {
  if (user.liveContext) return user.liveContext;
  if (user.currentPageLabel) return user.currentPageLabel;
  if (user.currentPath) return pageLabel(user.currentPath);
  return '';
}
